import type {
  GeneratedIntakeFieldResponse,
  IntakeHubDocumentExtractedFieldResponse,
  IntakeHubDocumentFieldSuggestionResponse,
  IntakeHubDocumentInventoryItemResponse,
  IntakeHubDocumentItemLinkResponse,
} from '@store/generated/intake-hub';

type SuggestionDisplayField = Pick<
  GeneratedIntakeFieldResponse,
  'data_type' | 'destination' | 'field_id' | 'is_writable' | 'label'
>;

const SENSITIVE_SUGGESTION_DATA_TYPES = new Set(['file', 'secret', 'ssn']);

export function canDisplayDocumentSuggestion(field: SuggestionDisplayField): boolean {
  return (
    field.is_writable !== false &&
    field.destination.kind === 'intake_item_evidence' &&
    !SENSITIVE_SUGGESTION_DATA_TYPES.has(field.data_type.toLowerCase())
  );
}

export interface IntakeDocumentSuggestedField {
  label: string;
  suggestion: IntakeHubDocumentFieldSuggestionResponse;
}

export interface IntakeDocumentDetail {
  id: string;
  sourceArtifactId?: string | null;
  /** What the document is called on screen — see `intakeDocumentDisplayName`. */
  title: string;
  /** The other of filename/section title, when both carry information. */
  secondaryName?: string;
  docKind?: string | null;
  documentDomain?: string | null;
  createdAt?: string | null;
  extractedFields?: IntakeHubDocumentExtractedFieldResponse[];
  suggestedFields?: IntakeDocumentSuggestedField[];
  linkedItems?: IntakeHubDocumentItemLinkResponse[];
}

function humanize(value?: string | null): string {
  if (!value) return 'Document';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * The document reference ids that came out of a decomposed upload — one PDF
 * split into several documents. Every document reference from one artifact
 * carries that artifact's id, so more than one sharing a `source_artifact_id`
 * means the upload was decomposed.
 *
 * They all resolve to the same uploaded filename, so the filename cannot tell
 * them apart; the LLM's section title is what distinguishes them.
 */
export function decomposedDocumentReferenceIds(
  documents: readonly IntakeHubDocumentInventoryItemResponse[],
): Set<string> {
  const byArtifact = new Map<string, string[]>();
  for (const documentItem of documents) {
    const artifactId = documentItem.document_reference.source_artifact_id;
    if (!artifactId) continue;
    byArtifact.set(artifactId, [
      ...(byArtifact.get(artifactId) ?? []),
      documentItem.document_reference.document_reference_id,
    ]);
  }
  return new Set([...byArtifact.values()].filter(referenceIds => referenceIds.length > 1).flat());
}

export interface IntakeDocumentDisplayName {
  primary: string;
  secondary?: string;
}

/**
 * What a document is called on screen.
 *
 * The real uploaded filename wins by default — a provider recognises
 * `CA_license_scan.pdf`, not the LLM's "State License". The exception is a
 * decomposed upload, where every child shares one filename and only the
 * section title separates them; there the title leads and the filename is
 * secondary. Falls back to the title, then to a humanized `doc_kind`, so an
 * in-app-authored document (no filename) still reads sensibly.
 */
export function intakeDocumentDisplayName(
  documentItem: IntakeHubDocumentInventoryItemResponse,
  options: { isDecomposedChild?: boolean } = {},
): IntakeDocumentDisplayName {
  const document = documentItem.document_reference;
  const filename = documentItem.original_filename?.trim() || undefined;
  const title = document.title?.trim() || undefined;
  if (options.isDecomposedChild) {
    const primary = title ?? filename ?? humanize(document.doc_kind);
    return { primary, ...(filename && filename !== primary ? { secondary: filename } : {}) };
  }
  const primary = filename ?? title ?? humanize(document.doc_kind);
  return { primary, ...(title && title !== primary ? { secondary: title } : {}) };
}

export function intakeDocumentInventoryItemToDetail(
  documentItem: IntakeHubDocumentInventoryItemResponse,
  fields: readonly SuggestionDisplayField[] = [],
  options: { isDecomposedChild?: boolean } = {},
): IntakeDocumentDetail {
  const document = documentItem.document_reference;
  const displayName = intakeDocumentDisplayName(documentItem, options);
  const visibleFields = new Map(
    fields.filter(canDisplayDocumentSuggestion).map(field => [field.field_id, field]),
  );
  return {
    id: document.document_reference_id,
    sourceArtifactId: document.source_artifact_id,
    title: displayName.primary,
    ...(displayName.secondary ? { secondaryName: displayName.secondary } : {}),
    docKind: document.doc_kind,
    documentDomain: document.document_domain,
    createdAt: document.created_at,
    extractedFields: documentItem.extracted_fields,
    // Fail closed when field metadata is unavailable. A suggestion value must
    // never become visible merely because an inventory endpoint returned it.
    suggestedFields: (documentItem.field_suggestions ?? [])
      .filter(suggestion => visibleFields.has(suggestion.field_id))
      .map(suggestion => ({
        label: visibleFields.get(suggestion.field_id)?.label ?? 'Suggested field',
        suggestion,
      })),
    linkedItems: documentItem.linked_items,
  };
}
